import { apiClient } from '@/lib/api/client'
import type { ClientSummary } from '@/store/clientContext'

interface AvailableClientsResponse {
  success: boolean
  data: ClientSummary[]
  current_client?: ClientSummary | null
  message?: string
}

interface SwitchClientResponse {
  success: boolean
  message?: string
  data?: {
    client: ClientSummary
    database?: string
  }
}

interface CurrentClientResponse {
  success: boolean
  data: ClientSummary | null
}

function normalizeClientList(raw: unknown): ClientSummary[] {
  if (Array.isArray(raw)) {
    return raw as ClientSummary[]
  }
  if (raw && typeof raw === 'object' && 'data' in raw) {
    const inner = (raw as { data: unknown }).data
    if (Array.isArray(inner)) return inner as ClientSummary[]
  }
  return []
}

export async function fetchAvailableClients(): Promise<{
  clients: ClientSummary[]
  currentClient: ClientSummary | null
}> {
  const { data } = await apiClient.get<AvailableClientsResponse>('/organization/clients/available')
  let clients = normalizeClientList(data.data ?? data)

  if (clients.length === 0) {
    try {
      const listRes = await apiClient.get('/organization/clients', {
        params: { per_page: 1000 },
      })
      // Some endpoints return { data: [...] }, others return paginated { data: { data: [...] } }
      const payload = listRes.data?.data?.data ?? listRes.data?.data ?? listRes.data
      clients = normalizeClientList(payload)
    } catch {
      // keep empty
    }
  }

  const current = data.current_client ?? null
  if (clients.length === 0 && current) {
    clients = [current]
  }

  return {
    clients,
    currentClient: current,
  }
}

/**
 * Fetch full client list for dropdown/options usage.
 * Uses organization clients endpoint (not active client context dependent).
 */
export async function fetchClientOptionsList(): Promise<ClientSummary[]> {
  try {
    // Follow options route pattern for dropdown lists
    const listRes = await apiClient.get('/options/clients')
    const payload = listRes.data?.data ?? listRes.data
    const clients = normalizeClientList(payload)
    if (clients.length > 0) {
      return clients
    }
  } catch {
    // fallback below
  }

  try {
    const listRes = await apiClient.get('/organization/clients', {
      params: { per_page: 1000, page: 1 },
    })
    const payload = listRes.data?.data?.data ?? listRes.data?.data ?? listRes.data
    const clients = normalizeClientList(payload)
    if (clients.length > 0) {
      return clients
    }
  } catch {
    // fallback below
  }

  const { clients } = await fetchAvailableClients()
  return clients
}

export async function switchClient(clientCode: string): Promise<ClientSummary | null> {
  const { data } = await apiClient.post<SwitchClientResponse>('/organization/clients/switch', {
    client_code: clientCode,
  })
  return data.data?.client ?? null
}

export async function fetchCurrentClient(): Promise<ClientSummary | null> {
  const { data } = await apiClient.get<CurrentClientResponse>('/organization/clients/current')
  return data.data ?? null
}
